import { Nominal } from 'lightweight-charts';
import { ensureNever } from '../helpers/assertions';
import { makeFont } from '../helpers/make-font';

import { HoveredObject } from '../model/chart-model';
import { Coordinate } from '../model/coordinate';
import { SeriesMarkerShape, SeriesMarkerText, PositionOffset, OffsetDirection } from '../model/series-markers';
import { TextWidthCache } from '../model/text-width-cache';
import { SeriesItemsIndexesRange, TimedValue } from '../model/time-data';

import { ScaledRenderer } from './scaled-renderer';
import { drawArrow, hitTestArrow } from './series-markers-arrow';
import { drawCircle, hitTestCircle } from './series-markers-circle';
import { drawSquare, hitTestSquare, drawLine } from './series-markers-square';
import { drawText, hitTestText } from './series-markers-text';

export interface SeriesMarkerRendererDataItem extends TimedValue {
  y: Coordinate;
  size: Coordinate;
  shape: SeriesMarkerShape;
  color: string;
  internalId: number;
  externalId?: string;
  text?: SeriesMarkerText;
  text1?: string;
  font?: string;
  textColor?: string;
  textColor1?: string;
  offset?: PositionOffset
}

export interface SeriesMarkerRendererData {
  items: SeriesMarkerRendererDataItem[];
  visibleRange: SeriesItemsIndexesRange | null;
}

export class SeriesMarkersRenderer extends ScaledRenderer {
  private _data: SeriesMarkerRendererData | null = null;
  private _textWidthCache: TextWidthCache = new TextWidthCache();
  private _fontSize: number = -1;
  private _fontFamily: string = '';
  private _font: string = '';

  public setData(data: SeriesMarkerRendererData): void {
    this._data = data;
  }

  public setParams(fontSize: number, fontFamily: string): void {
    if (this._fontSize !== fontSize || this._fontFamily !== fontFamily) {
      this._fontSize = fontSize;
      this._fontFamily = fontFamily;
      this._font = makeFont(fontSize, fontFamily);
      this._textWidthCache.reset();
    }
  }

  public hitTest(x: Coordinate, y: Coordinate): HoveredObject | null {
    if (this._data === null || this._data.visibleRange === null) {
      return null;
    }

    for (let i = this._data.visibleRange.from; i < this._data.visibleRange.to; i++) {
      const item = this._data.items[i];
      if (hitTestItem(item, x, y)) {
        return {
          hitTestData: item.internalId,
          externalId: item.externalId,
        };
      }
    }

    return null;
  }

  protected _drawImpl(ctx: CanvasRenderingContext2D, isHovered: boolean, hitTestData?: unknown): void {
    if (this._data === null || this._data.visibleRange === null) {
      return;
    }

    ctx.textBaseline = 'middle';
    ctx.font = "16px Arial";// this._font;

    for (let i = this._data.visibleRange.from; i < this._data.visibleRange.to; i++) {
      const item = this._data.items[i];
      if (item.text !== undefined) {
        item.text.width = this._textWidthCache.measureText(ctx, item.text.content);
        item.text.height = this._fontSize;
        item.text.x = item.text.x - item.text.width / 2 as Coordinate;
      }
      drawItem(item, ctx);
    }
  }
}

function drawItem(item: SeriesMarkerRendererDataItem, ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = item.color;

  drawShape(item, ctx);

  item.text.x = Number(item.x)+ (item.offset.x?item.offset.x:0) as Nominal<number, "Coordinate">;

  if (item.font) {
    //   if(item.font.includes("underline"))debugger;

    ctx.font = item.font;


  }

  drawText(ctx, item.text);

  if (item.shape == "textMarker") {
    const clone = Object.assign({}, item);


    ctx.fillStyle = clone.textColor1;
    drawText(ctx, clone.text, item, (item.font) ? item.font.includes("underline") : false);

  }





}

function drawShape(item: SeriesMarkerRendererDataItem, ctx: CanvasRenderingContext2D): void {
  if (item.size === 0) {
    return;
  }

  switch (item.shape) {
    case 'arrowDown':
      drawArrow(false, ctx, item.x, item.y, item.size);
      return;
    case 'arrowUp':
      drawArrow(true, ctx, item.x, item.y, item.size);
      return;
    case 'circle':
      drawCircle(ctx, item.x, item.y, item.size);
      return;
    case 'square':
      drawSquare(ctx, item.x, item.y, item.size);
      return;
    case 'line':
      drawLine(ctx, item.x, item.y, item.size);
      return;
    case 'textMarker':
      return
  }

  ensureNever(item.shape);
}

function hitTestItem(item: SeriesMarkerRendererDataItem, x: Coordinate, y: Coordinate): boolean {
  if (item.text !== undefined && hitTestText(item.text, x, y)) {
    return true;
  }

  return hitTestShape(item, x, y);
}

function hitTestShape(item: SeriesMarkerRendererDataItem, x: Coordinate, y: Coordinate): boolean {
  if (item.size === 0) {
    return false;
  }

  switch (item.shape) {
    case 'arrowDown':
      return hitTestArrow(true, item.x, item.y, item.size, x, y);
    case 'arrowUp':
      return hitTestArrow(false, item.x, item.y, item.size, x, y);
    case 'circle':
      return hitTestCircle(item.x, item.y, item.size, x, y);
    case 'square':
      return hitTestSquare(item.x, item.y, item.size, x, y);
    case 'textMarker':
      return
    case 'line':
      return
  }

  ensureNever(item.shape);
}
